import {
  getColorTokenFallback,
  getColorToken,
  getColorTokenWithOpacity,
  getColorTokenWithOpacityFallback,
} from "./__internal__/utils";
import { ColorTokenName } from "./types";

/**
 * For a given alphaDecimal opacity, returns a tagged template which accepts a TS token name and returns CSS where any style referencing
 *   the token has alphaDecimal opacity applied via relative css colors (90%+ browser support), with a fallback where opacity is added by
 *   transforming underlying token value (for <10% browser support).
 *
 * Only use when the alphaDecimal opacity is not known at compile time. Prefer using cssWithOpacity[#] functions instead.
 * @arg alphaDecimal - a number in the range [0, 1]
 * @returns tagged template which accepts TS token name
 *
 * @example
 * ```
 * const MyDiv = styled.div`
 *   ${buildCssWithOpacity(0.25)`background-color: ${"Action.surface.default"}`};
 * `;
 * ```
 */
export const buildCssWithOpacity =
  <T extends string = ColorTokenName>(alphaDecimal: number) =>
  (template: TemplateStringsArray, tokenNameOrColor: T) => {
    const leadingCss = template[0]?.split(";");
    const trailingCss = template[1]?.split(";");
    const prefixCss = leadingCss?.at(-1);
    const suffixCss = trailingCss?.at(0);
    const leadingCssExcludingPrefixCss = leadingCss?.slice(0, -1);
    const trailingCssExcludingSuffixCss = trailingCss?.slice(1);
    const colorTokenFallbackOrColor = (getColorTokenFallback(
      tokenNameOrColor
    ) || tokenNameOrColor) as string;
    const colorTokenOrColor = (getColorToken(tokenNameOrColor) ||
      tokenNameOrColor) as string;
    const colorWithOpacity = getColorTokenWithOpacity(
      colorTokenOrColor,
      alphaDecimal
    );
    const colorWithOpacityFallback =
      getColorTokenWithOpacityFallback(
        colorTokenFallbackOrColor,
        alphaDecimal
      ) ?? "";
    const fallbackCss = colorWithOpacityFallback
      ? `${prefixCss}${colorWithOpacityFallback}${suffixCss}`.trim()
      : "";
    const preferredCss = `${prefixCss}${colorWithOpacity}${suffixCss}`;
    let result = [
      ...(leadingCssExcludingPrefixCss ?? []),
      fallbackCss,
      preferredCss,
      ...(trailingCssExcludingSuffixCss ?? []),
    ]
      .filter((part) => !!part.trim())
      .join(";\n")
      .trim();
    if (!result.endsWith(";")) {
      result += ";";
    }
    return result;
  };
